import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Supplier } from "../models/supplier";
import { RootState } from "./store";
import axios from "axios";
import { environment } from "../environments/environment";
import { AgreementReturnModel } from "../models/agreement";
import { WeeklyTimelineDay } from "../models/weeklyTimeline";
import { processAgreements } from "./locationSlice";
import { IOemSubscription } from "../models/person";

const currentDate = new Date().toISOString();
// API Calls Start
const apiGetSupplierData = async () => {
    let params = '&fields=all,contacts,parts,parts.all,';

    const response = await axios.get(`${environment.apiURL}/suppliers?${params}`);
    return response.data;
}

const apiGetSupplierDataById = async (supplierId: number, signal: AbortSignal): Promise<Supplier> => {
    const response = await axios.get(`${environment.apiURL}/suppliers/${supplierId}`, {signal});
    return response.data;
}

const apiGetSuggestedLocations = async (id: number, signal?: AbortSignal): Promise<number[]> => {
    const response = await axios.get(`${environment.apiURL}/locations/suggestedLocations?penetrationMatrixId=${id}`, {signal});
    return response.data;
}

const apiGetLocationById = async (id: number): Promise<any> => {
    const response = await axios.get(`${environment.apiURL}/locations/${id}`);
    return response.data;
}

const apiGetAgreementsBySupplier = async (id: number): Promise<AgreementReturnModel[]> => {
    const currentFormattedDate = new Date().toLocaleDateString('en-CA');
    const response = await axios.get(`${environment.apiURL}/customer-data/agreements?supplier=${id}&startDate=${currentFormattedDate}&endDate=${currentFormattedDate}&fields=all,supplier,location,location.all,location.agreement.all,location.oemPicture.all,location.oem.all, location.weekendProduction.all, location.plantShutdowns.all, location.platforms.all, location.regionalManager, location.regionalManager.all, location.regionalManager.photoAttachment.all, assignedReps.all, assignedReps.representative.all, assignedReps.representative.photoAttachment.all, assignedReps.representative.manager.id, assignedReps.representative.manager.type, assignedReps.representative.manager.firstName, assignedReps.representative.manager.lastName, assignedReps.representative.manager.email, assignedReps.representative.manager.phone, assignedReps.representative.manager.cell, assignedReps.representative.manager.photoAttachment.all`);
    return response.data;
}

const apiGetOEMs = async (supplierId: number): Promise<AgreementReturnModel[]> => {
    const response = await axios.get(`${environment.apiURL}/customer-data/agreements?supplier=${supplierId}&fields=all,supplier,location,location.all,location.agreement.all,location.oemPicture.all,location.oem,location.oem.all&startDate=${currentDate}&endDate=${currentDate}`);
    return response.data;
}

const apiUpdateSupplierOem = async (data: {id: number, contacts: IOemSubscription[]}): Promise<any> => {
    const response = await axios.post(`${environment.apiURL}/suppliers?fields=all,contacts,contacts.oemSubscriptions.all`, data);
    return response.data;
}

const apiGetWeeklyTimeline = async (
    locationId: number,
    agreementIds: number[],
    supplierId: number,
    dateBefore: Date | undefined,
    dateAfter: Date | undefined,
    signal?: AbortSignal,  // Adding signal for AbortController
): Promise<WeeklyTimelineDay[]> => {
    const localTimezoneOffset = new Date().getTimezoneOffset();

    // Set each date to start/end of day respectively
    dateBefore?.setHours(23, 59, 59, 999);
    dateAfter?.setHours(0, 0, 0, 0);

    // Format dates without timezone conversion
    const formattedBefore = dateBefore ? 
      `${dateBefore.getFullYear()}-${String(dateBefore.getMonth() + 1).padStart(2, '0')}-${String(dateBefore.getDate()).padStart(2, '0')}` : 
      undefined;
    const formattedAfter = dateAfter ? 
      `${dateAfter.getFullYear()}-${String(dateAfter.getMonth() + 1).padStart(2, '0')}-${String(dateAfter.getDate()).padStart(2, '0')}` : 
      undefined;

    const response = await axios.get(
      `${environment.apiURL}/customer-data/locations/weekly-timeline?fields=all&location=${locationId}&before=${formattedBefore}&after=${formattedAfter}&supplier=${supplierId}&timezoneOffset=${localTimezoneOffset}&agreements=[${agreementIds?.toString()}]`,
      {
        signal,  // Passing the abort signal to axios
      },
    );
    return response.data;
}

const apiGetAllWeeklyTimeline = async (
    locationIds: number[],
    supplierId: number,
    dateBefore: Date | undefined,
    dateAfter: Date | undefined,
    signal?: AbortSignal,  // Adding signal for AbortController
    agreementIds?: number[],
): Promise<{location: number, items: WeeklyTimelineDay[]}[]> => {
    const localTimezoneOffset = new Date().getTimezoneOffset();

    if (locationIds.length === 0) {
      return [];
    }

    // Set each date to start/end of day respectively
    dateBefore?.setHours(23, 59, 59, 999);
    dateAfter?.setHours(0, 0, 0, 0);

    // Format dates without timezone conversion
    const formattedBefore = dateBefore ? 
      `${dateBefore.getFullYear()}-${String(dateBefore.getMonth() + 1).padStart(2, '0')}-${String(dateBefore.getDate()).padStart(2, '0')}` : 
      undefined;
    const formattedAfter = dateAfter ? 
      `${dateAfter.getFullYear()}-${String(dateAfter.getMonth() + 1).padStart(2, '0')}-${String(dateAfter.getDate()).padStart(2, '0')}` : 
      undefined;

    const response = await axios.get(
      `${environment.apiURL}/customer-data/locations/weekly-timeline?fields=all&locations=[${locationIds.toString()}]&before=${formattedBefore}&after=${formattedAfter}&supplier=${supplierId}&timezoneOffset=${localTimezoneOffset}&agreements=[${agreementIds?.toString()}]`,
      {
        signal,  // Passing the abort signal to axios
      },
    );
    return response.data;
}
// API Calls End

// Thunks Start
export const getSupplierData = createAsyncThunk(
    'supplier/getSupplierData',
    async () => {
        const response = await apiGetSupplierData();
        return response;
    }
);

export const getSupplierDataById = createAsyncThunk(
    'supplier/getById',
    async (data: {supplierId: number, signal: AbortSignal}) => {
        const supplierData = await apiGetSupplierDataById(data.supplierId, data.signal);
        return supplierData;
    }
);

export const getSuggestedLocations = createAsyncThunk(
    'supplier/getSuggestedLocations',
    async (data: {supplierId: number, signal: AbortSignal}) => {
        const supplierData = await apiGetSuggestedLocations(data.supplierId, data.signal);
        return supplierData;
    }
);

export const getLocationById = createAsyncThunk(
    'supplier/getLocationById',
    async (id: number) => {
        const supplierData = await apiGetLocationById(id);
        return supplierData;
    }
)

export const getAgreementsBySupplier = createAsyncThunk(
    'supplier/getAgreementsBySupplier',
    async (supplierId: number) => {
        const response = await apiGetAgreementsBySupplier(supplierId);
        return response;
    }
)

export const getOEMs = createAsyncThunk(
    'supplier/getOEMs',
    async (supplierId: number) => {
        const response = await apiGetOEMs(supplierId);
        return response;
    }
)

export const updateSupplierOem = createAsyncThunk(
    'supplier/updateSupplierOem',
    async (data: {id: number, contacts: IOemSubscription[]}) => {
        const response = await apiUpdateSupplierOem(data);
        return response;
    }
)

export const getWeeklyTimeline = createAsyncThunk(
    'supplier/getWeeklyTimeline',
    async (data: {locationId: number, agreementIds: number[], supplierId: number, dateBefore: Date, dateAfter: Date, signal: AbortSignal}) => {
        const supplierData = await apiGetWeeklyTimeline(data.locationId, data.agreementIds, data.supplierId, data.dateBefore, data.dateAfter, data.signal);
        return supplierData;
    }
)

export const getAllWeeklyTimeline = createAsyncThunk(
    'supplier/getAllWeeklyTimeline',
    async (data: {locationIds: number[], supplierId: number, dateBefore: Date, dateAfter: Date, signal: AbortSignal, agreementIds: number[]}) => {
        const supplierData = await apiGetAllWeeklyTimeline(data.locationIds, data.supplierId, data.dateBefore, data.dateAfter, data.signal, data.agreementIds);
        return supplierData;
    }
)

// Create a new thunk to handle supplier selection and agreements
export const selectSupplierWithAgreements = createAsyncThunk(
    'supplier/selectSupplierWithAgreements',
    async (supplier: Supplier | null, { dispatch }) => {
        dispatch(setSelectedSupplier(supplier));
        
        if (supplier) {
            await dispatch(getAgreementsBySupplier(supplier.id));
        }

        dispatch(processAgreements());
        
        return supplier;
    }
);
// Thunks End

// Slice Start
interface SupplierState {
    selectedSupplier: Supplier | null;
    selectedProducingFacilities: Supplier[];
    selectedProducingFacilitiesFiltered: Supplier[];
    initialLoadComplete: boolean;
    allSuppliers: Supplier[];
    parentSuppliers: Supplier[];
    producingFacilities: Supplier[];
    agreements: AgreementReturnModel[];
    isLoading: boolean;
    error: string | null;
}

const initialState: SupplierState = {
    selectedSupplier: null,
    selectedProducingFacilities: [],
    selectedProducingFacilitiesFiltered: [],
    initialLoadComplete: false,
    allSuppliers: [],
    parentSuppliers: [],
    producingFacilities: [],
    agreements: [],
    isLoading: false,
    error: null
};

const supplierSlice = createSlice({
    name: "supplier",
    initialState,
    reducers: {
        setSelectedSupplier: (state, action) => {
            state.selectedSupplier = action.payload;
            // Move producing facilities logic here
            if (action.payload) {
                const facilities = state.allSuppliers
                    .filter((s) => s.parentSupplier === action.payload.id)
                    .map((s) => ({ ...s }));

                facilities.forEach((f) => {
                    if (f.parentSupplier || !f.isParentSupplier) {
                        const parentNameSplit = action.payload.name.split(" ");
                        parentNameSplit.forEach((word: string) => {
                            const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            const wordReplace = new RegExp(escapedWord, "gi");
                            f.name = f.name
                                .replace(wordReplace, "")
                                .trimStart()
                                .replace(/^-/, "")
                                .trim();
                        });
                    }
                });

                state.producingFacilities = facilities;

                // Set initial selected producing facilities if none exist
                if (state.selectedProducingFacilities.length === 0) {
                    const storedFacilities = localStorage.getItem("selected_producting_facilities");
                    state.selectedProducingFacilities = storedFacilities
                        ? JSON.parse(storedFacilities)
                        : state.allSuppliers.filter((s) => !s.isParentSupplier && s.parentSupplier != null);
                }
            }
            
            // Update filtered facilities when supplier changes
            state.selectedProducingFacilitiesFiltered = state.selectedProducingFacilities.filter(
                (s) => s?.parentSupplier === action.payload?.id
            );
        },
        setSelectedProducingFacilities: (state, action) => {
            state.selectedProducingFacilities = action.payload;
            state.selectedProducingFacilitiesFiltered = action.payload;
        },
        setInitialLoadComplete: (state, action) => {
            state.initialLoadComplete = action.payload;
        },
        setProducingFacilities: (state, action) => {
            state.producingFacilities = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getSupplierData.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getSupplierData.fulfilled, (state, action) => {
                state.allSuppliers = action.payload;
                
                // Set parent suppliers
                let parents = action.payload.filter(
                    (s: Supplier) => s.isParentSupplier || s.parentSupplier === null,
                );

                // Add non parent supplier to parent suppliers if they are not already in the parent suppliers array and their parent supplier is in the parent suppliers array
                let nonParentSuppliers = action.payload.filter(
                    (s: Supplier) => !s.isParentSupplier && !parents.some((p: Supplier) => p.id === s.parentSupplier) && s.parentSupplier != null && !parents.some((p: Supplier) => p.id === s.id)
                );

                parents = [...parents, ...nonParentSuppliers];

                if (parents.length === 0) {
                    parents = action.payload;
                }

                state.parentSuppliers = parents;

                // Set initial selected supplier from localStorage or first parent
                const supplierItem = localStorage.getItem("selected_supplier");
                let localStorageSupplier: Supplier | null = (supplierItem && (supplierItem !== 'undefined'))
                    ? JSON.parse(supplierItem)
                    : null;

                if (!localStorageSupplier || !action.payload.map((s: Supplier) => s.id).includes(localStorageSupplier?.id ?? -1)) {
                    localStorageSupplier = parents[0];
                }

                // Use the existing setSelectedSupplier reducer logic
                supplierSlice.caseReducers.setSelectedSupplier(state, { 
                    payload: localStorageSupplier,
                    type: setSelectedSupplier.type 
                });
                
                state.isLoading = false;
            })
            .addCase(getSupplierData.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to fetch supplier data';
            })
            .addCase(getAgreementsBySupplier.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAgreementsBySupplier.fulfilled, (state, action) => {
                state.agreements = action.payload;
                state.isLoading = false;
            })
            .addCase(getAgreementsBySupplier.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to fetch agreements';
            });
    },
});
// Slice End

// Export actions and reducer
export const { setSelectedSupplier, setSelectedProducingFacilities, setInitialLoadComplete, setProducingFacilities } = supplierSlice.actions;

// Selectors
export const selectSelectedSupplier = (state: RootState) => state.supplier.selectedSupplier;
export const selectSelectedProducingFacilities = (state: RootState) => state.supplier.selectedProducingFacilities;
export const selectSelectedProducingFacilitiesFiltered = (state: RootState) => state.supplier.selectedProducingFacilitiesFiltered;
export const selectInitialLoadComplete = (state: RootState) => state.supplier.initialLoadComplete;
export const selectAllSuppliers = (state: RootState) => state.supplier.allSuppliers;
export const selectParentSuppliers = (state: RootState) => state.supplier.parentSuppliers;
export const selectProducingFacilities = (state: RootState) => state.supplier.producingFacilities;
export const selectIsLoading = (state: RootState) => state.supplier.isLoading;
export const selectError = (state: RootState) => state.supplier.error;
export const selectAgreements = (state: RootState) => state.supplier.agreements;

export default supplierSlice.reducer;
